<template>
  <SatellitesPage>
    <template v-if="landings.length" #actions>
      <button class="btn _bg-green sat-btn-inline" :disabled="limitReached" @click.prevent="openUpload()"><SatIcon name="plus" /> {{ $t("satellites.landings.upload") }}</button>
    </template>

    <div v-if="loading" class="d-flex justify-content-center py-5">
      <img :src="require('@/assets/images/rolling.svg')" alt="loader" class="loader" />
    </div>

    <template v-else>
      <div class="landings-head">
        <div>
          <p class="landings-description">{{ $t("satellites.landings.description", { size: maxSizeText }) }}</p>
          <div class="landings-counter" :class="{ _full: limitReached }">
            {{ $t("satellites.landings.counter", { count, limit }) }}
          </div>
        </div>
      </div>
      <p v-if="limitReached && landings.length" class="landings-limit-note">{{ $t("satellites.landings.limit_reached", { limit }) }}</p>

      <div v-if="!landings.length" class="landings-empty">
        <svg v-svg symbol="file" size="0 0 25 32" role="presentation" class="landings-empty__icon" />
        <span class="landings-empty__title">{{ $t("satellites.landings.empty_title") }}</span>
        <span class="landings-empty__text">{{ $t("satellites.landings.empty_description") }}</span>
        <button class="btn _bg-green mt-2" :disabled="limitReached" @click.prevent="openUpload()">{{ $t("satellites.landings.upload") }}</button>
      </div>

      <div v-else class="c-table mt-4">
        <div class="inner">
          <vue-good-table styleClass="table  _no-wrap" :columns="columns" :rows="landings">
            <template slot="table-column" slot-scope="props">
              <span v-show="props.column.field !== 'control'">{{ $t(props.column.label) }}</span>
            </template>

            <template slot="table-row" slot-scope="props">
              <template v-if="props.column.field === 'name'">
                <a href="#" class="landings-name" @click.prevent="openUpload(props.row)">{{ props.row.name }}</a>
                <div v-if="props.row.original_filename && props.row.original_filename !== props.row.name" class="landings-muted landings-filename">
                  {{ props.row.original_filename }}
                </div>
              </template>
              <template v-else-if="props.column.field === 'archive_size'">
                <span v-tooltip="{ content: $t('satellites.landings.unpacked_size', { size: formatBytes(props.row.unpacked_size) }), triggers: ['hover', 'click'] }">
                  {{ formatBytes(props.row.archive_size) }}
                </span>
              </template>
              <template v-else-if="['created_at', 'updated_at'].includes(props.column.field)">
                <span v-if="props.row[props.column.field]">{{ formatDate(props.row[props.column.field]) }}</span>
                <span v-else class="landings-muted">—</span>
              </template>
              <template v-else-if="props.column.field === 'used_by'">
                <LandingUsage v-if="isUsed(props.row.used_by)" :used-by="props.row.used_by" />
                <span v-else class="landings-muted">{{ $t("satellites.landings.not_used") }}</span>
              </template>
              <template v-else-if="props.column.field === 'control'">
                <div class="controls-wrapper">
                  <button @click="openUpload(props.row)" class="sat-icon-btn" :title="$t('satellites.landings.edit')">
                    <SatIcon name="edit" />
                  </button>
                  <button @click="askDelete(props.row)" class="sat-icon-btn _danger" :title="$t('satellites.delete')">
                    <SatIcon name="trash" />
                  </button>
                </div>
              </template>
              <template v-else>{{ props.formattedRow[props.column.field] }}</template>
            </template>
            <div class="d-flex justify-content-center" slot="emptystate">
              {{ $t("satellites.landings.empty_title") }}
            </div>
          </vue-good-table>
        </div>
      </div>
    </template>

    <!-- Upload of a new landing, or editing of an existing one (target): its name and, optionally, a new archive -->
    <modal @closed="resetUpload" :width="600" name="satelliteLandingUpload" height="auto" :scrollable="true" classes="creative-modal">
      <div class="pt-3 pb-4 px-4">
        <svg v-svg @click="$modal.hide('satelliteLandingUpload')" class="close" symbol="modal-close" size="0 0 15 15" role="info"></svg>
        <h3 class="modal-title pt-3">
          {{ target ? $t("satellites.landings.edit_title", { name: target.name }) : $t("satellites.landings.upload_title") }}
        </h3>

        <div class="form-item pb-0 pt-3">
          <div class="form-item__label">{{ $t("satellites.landings.name") }}</div>
          <div class="form-item__field">
            <input v-model="uploadName" type="text" :maxlength="nameMaxLength" :placeholder="namePlaceholder" :class="{ invalid: !!nameError }" />
            <small v-if="nameError" class="error">{{ nameError }}</small>
          </div>
        </div>

        <div class="form-item__label pt-3">{{ target ? $t("satellites.landings.archive_replace") : $t("satellites.landings.archive") }}</div>
        <p v-if="target && isUsed(target.used_by)" class="landings-modal-note">{{ $t("satellites.landings.replace_note") }}</p>

        <div
          class="landings-drop mt-3"
          :class="{ _active: dragActive, _invalid: !!fileError }"
          @dragenter.prevent="dragActive = true"
          @dragover.prevent="dragActive = true"
          @dragleave.prevent="dragActive = false"
          @drop.prevent="onDrop"
        >
          <input id="satellite-landing-file" ref="file" type="file" :accept="accept" @change="onFileChange" />
          <template v-if="file">
            <div class="landings-drop__file">
              <span class="landings-drop__file-name">{{ file.name }}</span>
              <span class="landings-muted">{{ formatBytes(file.size) }}</span>
            </div>
            <label for="satellite-landing-file" class="landings-drop__link">{{ $t("satellites.landings.choose_other") }}</label>
          </template>
          <template v-else>
            <svg v-svg symbol="add-file" size="0 0 12 16" role="info" class="landings-drop__icon" />
            <div>{{ target ? $t("satellites.landings.drop_replace") : $t("satellites.landings.drop") }}</div>
            <label for="satellite-landing-file" class="btn _border _green mt-2 landings-drop__button">{{ $t("satellites.landings.choose_file") }}</label>
          </template>
        </div>
        <small class="landings-muted d-block mt-2">{{ $t("satellites.landings.requirements", { size: maxSizeText, extensions: extensionsText }) }}</small>
        <small v-if="fileError" class="error d-block mt-1">{{ fileError }}</small>

        <div v-if="uploadError" class="landings-modal-error mt-3">{{ uploadError }}</div>

        <div class="text-center">
          <button class="btn mt-4 mr-3 _bg-gray" @click="$modal.hide('satelliteLandingUpload')">{{ $t("cancel") }}</button>
          <button class="btn mt-4 _bg-green landings-submit" :disabled="uploading || !canSubmit" @click="upload">
            <img v-if="uploading" src="@/assets/images/rolling-button.svg" class="loader-button" alt="loader" />
            {{ target ? $t("save") : $t("satellites.landings.upload") }}
          </button>
        </div>
      </div>
    </modal>

    <modal @closed="closeDelete" :width="550" name="satelliteLandingDelete" height="auto" :scrollable="true" classes="creative-modal">
      <div class="pt-3 pb-4 px-3 mx-auto d-block text-center">
        <svg v-svg @click="$modal.hide('satelliteLandingDelete')" class="close" symbol="modal-close" size="0 0 15 15" role="info"></svg>
        <h3 class="modal-title pt-3">{{ $t("satellites.landings.delete_confirm", { name: target ? target.name : "" }) }}</h3>
        <template v-if="deleteUsedBy">
          <p class="pt-3 landings-modal-error">{{ $t("satellites.landings.delete_used") }}</p>
          <LandingUsage :used-by="deleteUsedBy" class="landings-delete-usage" />
          <button class="btn mt-4 _bg-gray" @click="$modal.hide('satelliteLandingDelete')">{{ $t("satellites.landings.close") }}</button>
        </template>
        <template v-else>
          <p class="pt-2 landings-muted">{{ $t("satellites.landings.delete_note") }}</p>
          <button class="btn mt-4 mr-3 _bg-gray" @click="$modal.hide('satelliteLandingDelete')">{{ $t("cancel") }}</button>
          <button class="btn mt-4 _bg-green" :disabled="saving" @click="remove">{{ $t("satellites.delete") }}</button>
        </template>
      </div>
    </modal>
  </SatellitesPage>
</template>

<script>
import satelliteService from "@/api/satelliteService";
import SatellitesPage from "@/components/Satellites/SatellitesPage.vue";
import SatIcon from "@/components/Satellites/SatIcon.vue";
import LandingUsage from "@/components/Satellites/LandingUsage.vue";
import satelliteFeatureGuard from "@/components/Satellites/satelliteFeatureGuard";
import {
  formatBytes,
  LANDING_ARCHIVE_ACCEPT,
  LANDING_ARCHIVE_EXTENSIONS,
  LANDING_NAME_MAX_LENGTH,
  landingNameFromFile,
  MAX_LANDING_ARCHIVE_SIZE,
  validateLandingArchive,
} from "@/components/Satellites/landings";

const isOk = (res) => res && res.status >= 200 && res.status < 300;
const violationText = (violations, key) => (violations && Array.isArray(violations[key]) ? violations[key].join(" ") : "");

export default {
  name: "SatelliteLandings",
  components: { SatellitesPage, SatIcon, LandingUsage },
  beforeRouteEnter: satelliteFeatureGuard,
  data() {
    return {
      loading: true,
      landings: [],
      count: 0,
      limit: 0,
      saving: false,
      // Landing being edited or deleted
      target: null,
      // Upload modal
      file: null,
      uploadName: "",
      uploading: false,
      dragActive: false,
      violations: {},
      uploadError: "",
      // used_by of the landing that could not be deleted (409)
      deleteUsedBy: null,
      accept: LANDING_ARCHIVE_ACCEPT,
      nameMaxLength: LANDING_NAME_MAX_LENGTH,
      columns: [
        { label: "satellites.landings.columns.name", field: "name", sortable: false },
        { label: "satellites.landings.columns.size", field: "archive_size", sortable: false },
        { label: "satellites.landings.columns.files", field: "files_count", sortable: false },
        { label: "satellites.landings.columns.created_at", field: "created_at", sortable: false },
        { label: "satellites.landings.columns.updated_at", field: "updated_at", sortable: false },
        { label: "satellites.landings.columns.used_by", field: "used_by", sortable: false },
        { label: "", field: "control", sortable: false },
      ],
    };
  },
  computed: {
    limitReached() {
      return this.limit > 0 && this.count >= this.limit;
    },
    maxSizeText() {
      return formatBytes(MAX_LANDING_ARCHIVE_SIZE);
    },
    extensionsText() {
      return LANDING_ARCHIVE_EXTENSIONS.join(", ");
    },
    clientFileError() {
      if (!this.file) return "";
      const error = validateLandingArchive(this.file);
      return error ? this.$t(error[0], error[1] || {}) : "";
    },
    fileError() {
      return this.clientFileError || violationText(this.violations, "file");
    },
    nameError() {
      return violationText(this.violations, "name");
    },
    nameChanged() {
      return !!this.target && !!this.uploadName.trim() && this.uploadName.trim() !== this.target.name;
    },
    canSubmit() {
      if (this.clientFileError) return false;
      return this.target ? !!this.file || this.nameChanged : !!this.file;
    },
    namePlaceholder() {
      return this.file ? landingNameFromFile(this.file.name) : this.$t("satellites.landings.name_placeholder");
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    formatBytes,
    formatDate(value) {
      return this.$moment(value).format("YYYY-MM-DD HH:mm");
    },
    isUsed(usedBy) {
      return !!usedBy && (usedBy.routers || []).length > 0;
    },
    showError(res) {
      this.$alert({ type: "error", title: (res && res.message) || this.$t("server_error") });
    },
    fetch() {
      return satelliteService
        .getLandings()
        .then((res) => {
          if (isOk(res)) {
            const data = res.data || {};
            this.landings = Array.isArray(data.landings) ? data.landings : [];
            this.count = Number.isInteger(data.count) ? data.count : this.landings.length;
            this.limit = Number.isInteger(data.limit) ? data.limit : 0;
          } else if (res && res.status === 403) {
            this.$router.replace({ name: "Dashboard" }).catch(() => {});
          } else {
            this.showError(res);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Upload / replace
    openUpload(landing = null) {
      if (!landing && this.limitReached) return;
      this.resetUpload();
      this.target = landing;
      this.uploadName = landing ? landing.name : "";
      this.$modal.show("satelliteLandingUpload");
    },
    resetUpload() {
      this.target = null;
      this.file = null;
      this.uploadName = "";
      this.violations = {};
      this.uploadError = "";
      this.dragActive = false;
      if (this.$refs.file) this.$refs.file.value = null;
    },
    setFile(file) {
      this.file = file || null;
      this.violations = {};
      this.uploadError = "";
    },
    onFileChange(event) {
      this.setFile(event.target.files && event.target.files[0]);
      // Lets the same file be chosen again after an error
      event.target.value = null;
    },
    onDrop(event) {
      this.dragActive = false;
      const files = event.dataTransfer && event.dataTransfer.files;
      if (files && files.length) this.setFile(files[0]);
    },
    // A new landing is uploaded with its name; an edited one is renamed and/or gets the new archive.
    upload() {
      if (this.uploading || !this.canSubmit) return;
      this.uploading = true;
      this.violations = {};
      this.uploadError = "";
      const target = this.target;
      let request;
      if (!target) {
        request = satelliteService.uploadLanding(this.file, this.uploadName.trim());
      } else {
        const rename = this.nameChanged ? satelliteService.renameLanding(target.id, this.uploadName.trim()) : Promise.resolve({ status: 200 });
        request = rename.then((res) => (isOk(res) && this.file ? satelliteService.replaceLandingArchive(target.id, this.file) : res));
      }
      request
        .then((res) => {
          if (isOk(res)) {
            this.$alert({ type: "success", title: this.$t(target ? "satellites.landings.saved" : "satellites.landings.uploaded") });
            this.$modal.hide("satelliteLandingUpload");
            this.fetch();
          } else if (res && res.status === 422 && res.data && res.data.violations) {
            this.violations = res.data.violations;
            if (!violationText(this.violations, "file") && !violationText(this.violations, "name")) this.uploadError = res.message || this.$t("server_error");
          } else if (res && res.status === 409) {
            this.uploadError = res.message || this.$t("satellites.landings.limit_reached", { limit: this.limit });
            this.fetch();
          } else if (res && res.status === 413) {
            this.uploadError = this.$t("satellites.landings.errors.size", { size: this.maxSizeText });
          } else {
            this.uploadError = (res && res.message) || this.$t("server_error");
          }
        })
        .finally(() => {
          this.uploading = false;
        });
    },

    // Delete
    askDelete(landing) {
      this.target = landing;
      this.deleteUsedBy = null;
      this.$modal.show("satelliteLandingDelete");
    },
    closeDelete() {
      this.target = null;
      this.deleteUsedBy = null;
    },
    remove() {
      if (!this.target || this.saving) return;
      this.saving = true;
      satelliteService
        .deleteLanding(this.target.id)
        .then((res) => {
          if (isOk(res)) {
            this.$alert({ type: "success", title: this.$t("satellites.landings.deleted") });
            this.$modal.hide("satelliteLandingDelete");
            this.fetch();
          } else if (res && res.status === 409 && res.data && res.data.used_by) {
            this.deleteUsedBy = res.data.used_by;
            this.fetch();
          } else {
            this.showError(res);
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.landings-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px 24px;
}

.landings-description {
  max-width: 720px;
  font-size: 13px;
  color: #9b9ba4;
}

.landings-counter {
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #141414;

  &._full {
    color: #a86a00;
  }
}

.landings-limit-note {
  margin-top: 8px;
  font-size: 13px;
  color: #a86a00;
}

.landings-name {
  display: block;
  color: var(--sat-accent);
  cursor: pointer;

  font-weight: 600;
  white-space: normal;
  word-break: break-word;
}

.landings-filename {
  font-size: 12px;
}

.landings-muted {
  color: #9b9ba4;
  font-size: 13px;
}

.landings-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
  padding: 60px 20px;
  text-align: center;
  background-color: #f7f7f9;
  border-radius: 15px;

  &__icon {
    width: 40px;
    height: 52px;
    fill: #37807d;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
  }

  &__text {
    max-width: 560px;
    font-size: 14px;
    line-height: 1.5;
    color: #5d5d6b;
  }
}

.landings-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 150px;
  padding: 24px;
  font-size: 14px;
  text-align: center;
  border: 2px dashed #d4d4dc;
  border-radius: 12px;
  transition: background-color 0.2s, border-color 0.2s;

  &._active {
    border-color: #37807d;
    background-color: #f5f9f9;
  }

  &._invalid {
    border-color: #f14343;
  }

  input[type="file"] {
    display: none;
  }

  &__icon {
    width: 24px;
    height: 32px;
    fill: #37807d;
  }

  &__button {
    display: inline-flex;
    align-items: center;
    height: 40px;
    padding: 0 18px;
    cursor: pointer;
  }

  &__file {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__file-name {
    font-weight: 600;
    word-break: break-all;
  }

  &__link {
    margin: 0;
    color: #317475;
    text-decoration: underline;
    cursor: pointer;
  }
}

.landings-modal-note {
  margin-top: 8px;
  font-size: 13px;
  color: #9b9ba4;
}

.landings-modal-error {
  font-size: 14px;
  color: #f14343;
}

.landings-delete-usage {
  display: inline-block;
  text-align: left;
}

.landings-submit {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.controls-wrapper {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  button:focus {
    outline: none;
  }

  .controls__btn {
    background: transparent;
  }
}

h3.modal-title {
  max-width: 80%;
  text-align: center;
  margin: 0 auto;
  word-break: break-word;
}

body.dark {
  .landings-counter {
    color: #dddddd;

    &._full {
      color: #e0a33c;
    }
  }

  .landings-empty {
    background-color: #151515;

    &__title {
      color: #dddddd;
    }

    &__text {
      color: #828289;
    }
  }

  .landings-drop {
    border-color: #3a3a42;
    color: #dddddd;

    &._active {
      border-color: #479495;
      background-color: #1f2a2a;
    }

    &__link {
      color: #479495;
    }
  }

  h3.modal-title {
    color: #dddddd;
  }
}
</style>
